import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';

export type MarketplaceMemberRole = 'owner' | 'admin' | 'developer' | 'moderator';

export interface MarketplaceMembership {
  id: string;
  product_id: string;
  user_id: string;
  role: MarketplaceMemberRole;
  created_at: string;
  updated_at: string;
}

export interface MarketplaceTeamMember extends MarketplaceMembership {
  profile: {
    id: string;
    full_name: string | null;
    email: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
}

const MANAGER_ROLES: MarketplaceMemberRole[] = ['owner', 'admin'];
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function sortMemberships(memberships: MarketplaceMembership[]) {
  return [...memberships].sort((left, right) => {
    const roleOrder: MarketplaceMemberRole[] = ['owner', 'admin', 'developer', 'moderator'];
    return roleOrder.indexOf(left.role) - roleOrder.indexOf(right.role);
  });
}

export function useMarketplaceMemberships() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [memberships, setMemberships] = useState<MarketplaceMembership[]>([]);
  const [loading, setLoading] = useState(!!user);
  const [error, setError] = useState<string | null>(null);

  const isSiteAdmin = (profile?.role || '').toLowerCase() === 'admin';

  const refresh = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: queryError } = await (supabase.from('marketplace_product_members') as any)
      .select('id, product_id, user_id, role, created_at, updated_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (queryError) {
      setMemberships([]);
      setError(queryError.message);
      setLoading(false);
      return;
    }

    setMemberships((data ?? []) as MarketplaceMembership[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const membershipsByProduct = useMemo(() => {
    const map = new Map<string, MarketplaceMembership>();
    memberships.forEach((membership) => {
      map.set(membership.product_id, membership);
    });
    return map;
  }, [memberships]);

  const accessibleProductIds = useMemo(
    () => new Set(memberships.map((membership) => membership.product_id)),
    [memberships]
  );

  const getRoleForProduct = useCallback(
    (productId: string) => membershipsByProduct.get(productId)?.role ?? null,
    [membershipsByProduct]
  );

  const canAccessProduct = useCallback(
    (productId: string) => isSiteAdmin || accessibleProductIds.has(productId),
    [accessibleProductIds, isSiteAdmin]
  );

  const canManageProduct = useCallback(
    (productId: string) => {
      if (isSiteAdmin) {
        return true;
      }

      const role = membershipsByProduct.get(productId)?.role;
      return !!role && MANAGER_ROLES.includes(role);
    },
    [isSiteAdmin, membershipsByProduct]
  );

  const canManageTeam = canManageProduct;

  const listMembers = useCallback(
    async (productId: string) => {
      const { data: membershipRows, error: membershipError } = await (supabase.from('marketplace_product_members') as any)
        .select('id, product_id, user_id, role, created_at, updated_at')
        .eq('product_id', productId)
        .order('created_at', { ascending: true });

      if (membershipError) {
        throw membershipError;
      }

      const membershipsForProduct = (membershipRows ?? []) as MarketplaceMembership[];
      const userIds = membershipsForProduct.map((membership) => membership.user_id);

      if (userIds.length === 0) {
        return [] as MarketplaceTeamMember[];
      }

      const { data: profileRows, error: profileError } = await (supabase.from('profiles') as any)
        .select('id, full_name, email, username, avatar_url')
        .in('id', userIds);

      if (profileError) {
        throw profileError;
      }

      const profileMap = new Map<string, MarketplaceTeamMember['profile']>();
      (profileRows ?? []).forEach((profileRow: any) => {
        profileMap.set(profileRow.id, profileRow);
      });

      return sortMemberships(
        membershipsForProduct.map((membership) => ({
          ...membership,
          profile: profileMap.get(membership.user_id) ?? null,
        }))
      ) as MarketplaceTeamMember[];
    },
    []
  );

  const addMember = useCallback(
    async (productId: string, userId: string, role: MarketplaceMemberRole) => {
      const { error: insertError } = await (supabase.from('marketplace_product_members') as any).insert({
        product_id: productId,
        user_id: userId,
        role,
      });

      if (insertError) {
        throw insertError;
      }

      await refresh();
    },
    [refresh]
  );

  const addMemberByEmail = useCallback(
    async (productId: string, emailOrUserId: string, role: MarketplaceMemberRole) => {
      const value = emailOrUserId.trim();
      if (!value) {
        throw new Error('Email or user ID is required.');
      }

      let targetProfile: { id: string } | null = null;

      if (UUID_PATTERN.test(value)) {
        const { data, error: lookupError } = await (supabase.from('profiles') as any)
          .select('id')
          .eq('id', value)
          .maybeSingle();

        if (lookupError) {
          throw lookupError;
        }

        targetProfile = data;
      } else {
        const { data, error: lookupError } = await (supabase.from('profiles') as any)
          .select('id')
          .ilike('email', value)
          .maybeSingle();

        if (lookupError) {
          throw lookupError;
        }

        targetProfile = data;
      }

      if (!targetProfile?.id) {
        throw new Error('No profile found for that email or user ID.');
      }

      await addMember(productId, targetProfile.id, role);
    },
    [addMember]
  );

  const updateMemberRole = useCallback(
    async (membershipId: string, role: MarketplaceMemberRole) => {
      const { error: updateError } = await (supabase.from('marketplace_product_members') as any)
        .update({ role })
        .eq('id', membershipId);

      if (updateError) {
        throw updateError;
      }

      await refresh();
    },
    [refresh]
  );

  const removeMember = useCallback(
    async (membershipId: string) => {
      const { error: deleteError } = await (supabase.from('marketplace_product_members') as any)
        .delete()
        .eq('id', membershipId);

      if (deleteError) {
        throw deleteError;
      }

      await refresh();
    },
    [refresh]
  );

  return {
    memberships,
    loading,
    error,
    refresh,
    isSiteAdmin,
    isVendor: isSiteAdmin || memberships.length > 0,
    accessibleProductIds,
    getRoleForProduct,
    canAccessProduct,
    canManageProduct,
    canManageTeam,
    listMembers,
    addMember,
    addMemberByEmail,
    updateMemberRole,
    removeMember,
  };
}
